import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Settings,
  ArrowBack,
  Save,
  Cancel,
  Star,
  StarBorder,
  Visibility,
  Print
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useApiOperation } from '../hooks/useApiOperation';
import { invoiceTemplateConfigService } from '../services/configurationService';
import {
  InvoiceTemplateConfig,
  InvoiceTemplateConfigCreate,
  InvoiceTemplateConfigUpdate,
  PrinterFormat
} from '../types/configuration';

const documentTypes = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'estimate', label: 'Estimate' },
  { value: 'sales_order', label: 'Sales Order' },
  { value: 'delivery_challan', label: 'Delivery Challan' },
  { value: 'purchase_order', label: 'Purchase Order' },
  { value: 'purchase_bill', label: 'Purchase Bill' }
];

const printerFormats = [
  { value: PrinterFormat.REGULAR, label: 'Regular (A4/Letter)' },
  { value: PrinterFormat.THERMAL, label: 'Thermal (80mm)' },
  { value: PrinterFormat.TALLY_PRIME, label: 'Tally Prime Compatible' }
];

const InvoiceTemplateConfigurationPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<InvoiceTemplateConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<InvoiceTemplateConfig | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const { execute: saveTemplate, loading: saving } = useApiOperation(
    async (data: InvoiceTemplateConfigCreate | InvoiceTemplateConfigUpdate) => {
      if (editingTemplate) {
        return await invoiceTemplateConfigService.update(editingTemplate.id, data as InvoiceTemplateConfigUpdate);
      } else {
        return await invoiceTemplateConfigService.create(data as InvoiceTemplateConfigCreate);
      }
    }
  );

  const { execute: deleteTemplate } = useApiOperation(
    async (id: string) => {
      await invoiceTemplateConfigService.delete(id);
    }
  );

  const { execute: setAsDefault } = useApiOperation(
    async (id: string) => {
      await invoiceTemplateConfigService.setAsDefault(id);
    }
  );

  const [formData, setFormData] = useState<InvoiceTemplateConfigCreate>({
    template_name: '',
    document_type: 'invoice',
    printer_format: PrinterFormat.REGULAR,
    company_name: '',
    company_address: '',
    show_logo: true,
    show_company_details: true,
    show_item_images: false,
    show_item_descriptions: true,
    show_hsn_codes: true,
    show_tax_breakdown: true,
    show_payment_terms: true,
    show_bank_details: true,
    show_signature_section: true,
    show_shipping_details: true,
    show_packing_details: false,
    show_transport_details: false,
    show_other_charges: true,
    is_default: false,
    is_active: true
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await invoiceTemplateConfigService.getList();
      // Response now has items field after transformation in service
      setTemplates(response.items || []);
    } catch (error: any) {
      console.error('Failed to load templates:', error);
      enqueueSnackbar(error.response?.data?.detail || 'Failed to load templates', { variant: 'error' });
      setTemplates([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (template?: InvoiceTemplateConfig) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        template_name: template.template_name,
        document_type: template.document_type,
        printer_format: template.printer_format,
        logo_url: template.logo_url,
        company_name: template.company_name,
        company_address: template.company_address,
        company_gstin: template.company_gstin,
        company_phone: template.company_phone,
        company_email: template.company_email,
        show_logo: template.show_logo,
        show_company_details: template.show_company_details,
        header_text: template.header_text,
        show_item_images: template.show_item_images,
        show_item_descriptions: template.show_item_descriptions,
        show_hsn_codes: template.show_hsn_codes,
        show_tax_breakdown: template.show_tax_breakdown,
        show_payment_terms: template.show_payment_terms,
        show_bank_details: template.show_bank_details,
        show_signature_section: template.show_signature_section,
        footer_text: template.footer_text,
        show_shipping_details: template.show_shipping_details,
        show_packing_details: template.show_packing_details,
        show_transport_details: template.show_transport_details,
        show_other_charges: template.show_other_charges,
        terms_and_conditions: template.terms_and_conditions,
        is_default: template.is_default,
        is_active: template.is_active
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        template_name: '',
        document_type: 'invoice',
        printer_format: PrinterFormat.REGULAR,
        company_name: '',
        company_address: '',
        show_logo: true,
        show_company_details: true,
        show_item_images: false,
        show_item_descriptions: true,
        show_hsn_codes: true,
        show_tax_breakdown: true,
        show_payment_terms: true,
        show_bank_details: true,
        show_signature_section: true,
        show_shipping_details: true,
        show_packing_details: false,
        show_transport_details: false,
        show_other_charges: true,
        is_default: false,
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTemplate(null);
  };

  const handleSave = async () => {
    try {
      await saveTemplate(formData);
      enqueueSnackbar(
        editingTemplate ? 'Template updated successfully' : 'Template created successfully',
        { variant: 'success' }
      );
      handleCloseDialog();
      loadTemplates();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to save template', { variant: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await deleteTemplate(id);
      enqueueSnackbar('Template deleted successfully', { variant: 'success' });
      loadTemplates();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to delete template', { variant: 'error' });
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await setAsDefault(id);
      enqueueSnackbar('Template set as default successfully', { variant: 'success' });
      loadTemplates();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to set as default', { variant: 'error' });
    }
  };

  const handlePreview = async (template: InvoiceTemplateConfig) => {
    try {
      const blob = await invoiceTemplateConfigService.previewTemplate(template.id);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewDialogOpen(true);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to generate preview', { variant: 'error' });
    }
  };

  const handleInputChange = (field: keyof InvoiceTemplateConfigCreate, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/configuration')}
            variant="outlined"
            size="small"
          >
            Back to Configuration
          </Button>
        </Stack>
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Settings fontSize="large" />
            <Box>
              <Typography variant="h4" component="h1">
                Invoice Template Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure document templates for different printer formats and business needs
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Template
          </Button>
        </Box>
      </Box>

      {/* Templates Grid */}
      <Grid container spacing={3}>
        {templates.map((template) => (
          <Grid item xs={12} md={6} lg={4} key={template.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" component="h2">
                    {template.template_name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {template.is_default && (
                      <Chip
                        icon={<Star />}
                        label="Default"
                        color="primary"
                        size="small"
                      />
                    )}
                    <Chip
                      label={template.is_active ? 'Active' : 'Inactive'}
                      color={template.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>

                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Document Type:
                    </Typography>
                    <Typography variant="body2">
                      {documentTypes.find(dt => dt.value === template.document_type)?.label}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Format:
                    </Typography>
                    <Typography variant="body2">
                      {printerFormats.find(pf => pf.value === template.printer_format)?.label}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Company:
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {template.company_name}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Enabled Features:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {template.show_logo && <Chip label="Logo" size="small" variant="outlined" />}
                    {template.show_item_images && <Chip label="Item Images" size="small" variant="outlined" />}
                    {template.show_hsn_codes && <Chip label="HSN Codes" size="small" variant="outlined" />}
                    {template.show_tax_breakdown && <Chip label="Tax Breakdown" size="small" variant="outlined" />}
                    {template.show_payment_terms && <Chip label="Payment Terms" size="small" variant="outlined" />}
                    {template.show_bank_details && <Chip label="Bank Details" size="small" variant="outlined" />}
                  </Box>
                </Stack>
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Box>
                  <Tooltip title="Preview Template">
                    <IconButton
                      size="small"
                      onClick={() => handlePreview(template)}
                    >
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Template">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(template)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Template">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(template.id)}
                      disabled={template.is_default}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title={template.is_default ? 'Already Default' : 'Set as Default'}>
                    <IconButton
                      size="small"
                      onClick={() => handleSetDefault(template.id)}
                      disabled={template.is_default}
                    >
                      {template.is_default ? <Star color="primary" /> : <StarBorder />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {templates.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No templates configured
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create your first invoice template to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Template
          </Button>
        </Paper>
      )}

      {/* Template Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTemplate ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Template Name"
                value={formData.template_name}
                onChange={(e) => handleInputChange('template_name', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={formData.document_type}
                  onChange={(e) => handleInputChange('document_type', e.target.value)}
                  label="Document Type"
                >
                  {documentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Printer Format</InputLabel>
                <Select
                  value={formData.printer_format}
                  onChange={(e) => handleInputChange('printer_format', e.target.value)}
                  label="Printer Format"
                >
                  {printerFormats.map((format) => (
                    <MenuItem key={format.value} value={format.value}>
                      {format.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Logo URL"
                value={formData.logo_url || ''}
                onChange={(e) => handleInputChange('logo_url', e.target.value)}
                placeholder="https://example.com/logo.png"
              />
            </Grid>

            {/* Company Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Company Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company GSTIN"
                value={formData.company_gstin || ''}
                onChange={(e) => handleInputChange('company_gstin', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Address"
                value={formData.company_address}
                onChange={(e) => handleInputChange('company_address', e.target.value)}
                multiline
                rows={3}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Phone"
                value={formData.company_phone || ''}
                onChange={(e) => handleInputChange('company_phone', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Email"
                value={formData.company_email || ''}
                onChange={(e) => handleInputChange('company_email', e.target.value)}
                type="email"
              />
            </Grid>

            {/* Display Options */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Display Options
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_logo}
                    onChange={(e) => handleInputChange('show_logo', e.target.checked)}
                  />
                }
                label="Show Logo"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_company_details}
                    onChange={(e) => handleInputChange('show_company_details', e.target.checked)}
                  />
                }
                label="Show Company Details"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_item_images}
                    onChange={(e) => handleInputChange('show_item_images', e.target.checked)}
                  />
                }
                label="Show Item Images"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_item_descriptions}
                    onChange={(e) => handleInputChange('show_item_descriptions', e.target.checked)}
                  />
                }
                label="Show Item Descriptions"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_hsn_codes}
                    onChange={(e) => handleInputChange('show_hsn_codes', e.target.checked)}
                  />
                }
                label="Show HSN Codes"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_tax_breakdown}
                    onChange={(e) => handleInputChange('show_tax_breakdown', e.target.checked)}
                  />
                }
                label="Show Tax Breakdown"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_payment_terms}
                    onChange={(e) => handleInputChange('show_payment_terms', e.target.checked)}
                  />
                }
                label="Show Payment Terms"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_bank_details}
                    onChange={(e) => handleInputChange('show_bank_details', e.target.checked)}
                  />
                }
                label="Show Bank Details"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_signature_section}
                    onChange={(e) => handleInputChange('show_signature_section', e.target.checked)}
                  />
                }
                label="Show Signature Section"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_shipping_details}
                    onChange={(e) => handleInputChange('show_shipping_details', e.target.checked)}
                  />
                }
                label="Show Shipping Details"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_packing_details}
                    onChange={(e) => handleInputChange('show_packing_details', e.target.checked)}
                  />
                }
                label="Show Packing Details"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_transport_details}
                    onChange={(e) => handleInputChange('show_transport_details', e.target.checked)}
                  />
                }
                label="Show Transport Details"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_other_charges}
                    onChange={(e) => handleInputChange('show_other_charges', e.target.checked)}
                  />
                }
                label="Show Other Charges"
              />
            </Grid>

            {/* Additional Text */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Additional Text
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Header Text"
                value={formData.header_text || ''}
                onChange={(e) => handleInputChange('header_text', e.target.value)}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Footer Text"
                value={formData.footer_text || ''}
                onChange={(e) => handleInputChange('footer_text', e.target.value)}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Terms and Conditions"
                value={formData.terms_and_conditions || ''}
                onChange={(e) => handleInputChange('terms_and_conditions', e.target.value)}
                multiline
                rows={4}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Status
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_default}
                    onChange={(e) => handleInputChange('is_default', e.target.checked)}
                  />
                }
                label="Set as Default Template"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            disabled={saving || !formData.template_name || !formData.company_name || !formData.company_address}
          >
            {editingTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Template Preview
        </DialogTitle>
        <DialogContent>
          {previewUrl && (
            <Box sx={{ width: '100%', height: '600px' }}>
              <iframe
                src={previewUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="Template Preview"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={() => window.open(previewUrl, '_blank')}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceTemplateConfigurationPage;